import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import type { TrialInfo } from "@/views/Portfolio/types";
import { PortfolioComplianceTrendsTab } from "@/views/PortfolioCompliance/PortfolioComplianceTrendsTab";
import { PortfolioEnrollmentTrendsTab } from "@/views/PortfolioEnrollment/PortfolioEnrollmentTrendsTab";
import { PortfolioMilestonesTimeline } from "@/views/PortfolioMilestones/PortfolioMilestonesTimeline";
import { PortfolioRetentionTrendsTab } from "@/views/PortfolioRetention/PortfolioRetentionTrendsTab";
import { PortfolioSafetyTrendsTab } from "@/views/PortfolioSafety/PortfolioSafetyTrendsTab";

import type {
  MetricRowTrialField,
  PortfolioMetricPanel,
} from "./portfolioMetricPanels.types";

export const METRICS_SCREEN_VIEW = "metrics";
export const SCREEN_VIEW_URL_PARAM = "view";
export const METRIC_URL_PARAM = "metric";

/**
 * Single source of truth for the rail, the URL parameter and the legacy-route
 * redirects, so the three cannot drift apart.
 */
export const PORTFOLIO_METRIC_PANELS: PortfolioMetricPanel[] = [
  {
    key: "enrollment",
    label: "Enrollment",
    featureFlag: FEATURE_FLAGS.PORT_ENROLLMENT,
    endpointSuffix: "enrollment_metrics",
    rowTrialField: "trial_id",
    component: PortfolioEnrollmentTrendsTab,
  },
  {
    key: "retention",
    label: "Retention",
    featureFlag: FEATURE_FLAGS.PORT_RETENTION,
    endpointSuffix: "retention_metrics",
    rowTrialField: "trial_id",
    component: PortfolioRetentionTrendsTab,
  },
  {
    key: "safety",
    label: "Safety",
    featureFlag: FEATURE_FLAGS.PORT_SAFETY,
    endpointSuffix: "safety_metrics",
    rowTrialField: "trial_id",
    component: PortfolioSafetyTrendsTab,
  },
  {
    key: "compliance",
    label: "Compliance",
    featureFlag: FEATURE_FLAGS.PORT_COMPLIANCE,
    endpointSuffix: "compliance_metrics",
    rowTrialField: "trial_id",
    component: PortfolioComplianceTrendsTab,
  },
  {
    key: "milestones",
    label: "Milestones",
    featureFlag: FEATURE_FLAGS.PORT_MILESTONES,
    endpointSuffix: "milestone_metrics",
    rowTrialField: "trial",
    component: PortfolioMilestonesTimeline,
  },
];

export const getEnabledMetricPanels = (
  isFlagEnabled: (flag: string) => boolean,
): PortfolioMetricPanel[] =>
  PORTFOLIO_METRIC_PANELS.filter((panel) => isFlagEnabled(panel.featureFlag));

export const resolveActiveMetricPanel = (
  panels: PortfolioMetricPanel[],
  requestedKey: string | null,
): PortfolioMetricPanel | undefined =>
  panels.find((panel) => panel.key === requestedKey) ?? panels[0];

const trialNamesForIds = (
  trials: TrialInfo[] | undefined,
  allowedTrialIds: ReadonlySet<string>,
): Set<string> =>
  new Set(
    (trials ?? [])
      .filter((trial) => allowedTrialIds.has(trial.id))
      .map((trial) => trial.name)
      .filter((name): name is string => typeof name === "string"),
  );

/** `allowedTrialIds` of null means no filtering is in effect: every row passes. */
export const filterMetricRowsToTrials = (
  rows: any[] | undefined,
  allowedTrialIds: ReadonlySet<string> | null,
  rowTrialField: MetricRowTrialField,
  trials: TrialInfo[] | undefined,
): any[] | undefined => {
  if (!Array.isArray(rows) || allowedTrialIds === null) return rows;

  const allowedValues =
    rowTrialField === "trial"
      ? trialNamesForIds(trials, allowedTrialIds)
      : allowedTrialIds;

  return rows.filter((row) => allowedValues.has(row?.[rowTrialField]));
};
